import React from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  DollarSign, Tag, Plus, Trash2,
  Type, List, ToggleLeft, Calendar, Hash, FileText
} from 'lucide-react';

const INPUT_TYPE_OPTIONS = [
  { value: 'text', label: 'Text Input', icon: Type },
  { value: 'number', label: 'Number Input', icon: Hash },
  { value: 'textarea', label: 'Text Area', icon: FileText },
  { value: 'date', label: 'Date Picker', icon: Calendar },
  { value: 'time', label: 'Time Picker', icon: Calendar },
  { value: 'checkbox', label: 'Checkbox', icon: ToggleLeft },
  { value: 'radio', label: 'Radio Buttons', icon: ToggleLeft },
  { value: 'dropdown', label: 'Dropdown', icon: List },
  { value: 'button-select', label: 'Button Select', icon: ToggleLeft },
  { value: 'image-cards', label: 'Image Cards', icon: FileText },
  { value: 'gallery', label: 'Image Gallery', icon: FileText },
  { value: 'file_upload', label: 'File Upload', icon: FileText },
];

/**
 * Single option editor card — name, type, pricing mode, and choices.
 */
const OptionEditorCard = ({ option, onUpdate, onUpdateChoice, onAddChoice, onRemoveChoice, onRemove }) => {
  const hasChoices = ['select', 'radio', 'checkbox', 'dropdown', 'button-select', 'image-cards', 'gallery'].includes(option.type);

  return (
    <Card className="border-gray-200">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 space-y-2">
            <Input
              data-testid={`option-name-${option.id}`}
              placeholder="Option Name (e.g., Storage Capacity, Color)"
              value={option.name}
              onChange={(e) => onUpdate(option.id, 'name', e.target.value)}
              className="font-medium border-gray-300"
            />
            <Input
              placeholder="Help text for customers (optional)"
              value={option.helpText}
              onChange={(e) => onUpdate(option.id, 'helpText', e.target.value)}
              className="text-sm border-gray-300"
            />
          </div>
          <Button
            data-testid={`remove-option-${option.id}`}
            variant="ghost"
            size="icon"
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => onRemove(option.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        {option.fromTemplate && (
          <Badge variant="outline" className="text-xs mt-1">Based on: {option.fromTemplate}</Badge>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Controls row */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Switch
              checked={option.priceImpact || false}
              onCheckedChange={(checked) => onUpdate(option.id, 'priceImpact', checked)}
            />
            <Label className="text-sm whitespace-nowrap flex items-center gap-1">
              <DollarSign className={`h-3.5 w-3.5 ${option.priceImpact ? 'text-green-600' : 'text-gray-400'}`} />
              {option.priceImpact ? 'Price Impact ON' : 'Info Only'}
            </Label>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">Input Type:</Label>
            <Select value={option.type || 'select'} onValueChange={(value) => onUpdate(option.id, 'type', value)}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INPUT_TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    <div className="flex items-center gap-2"><t.icon className="h-3 w-3" /><span>{t.label}</span></div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {option.priceImpact && <PricingControls option={option} onUpdate={onUpdate} />}
        </div>

        <PricingExplanation option={option} />

        {hasChoices && (
          <ChoicesEditor
            option={option}
            onUpdateChoice={onUpdateChoice}
            onAddChoice={onAddChoice}
            onRemoveChoice={onRemoveChoice}
          />
        )}

        {['text', 'textarea', 'number', 'date'].includes(option.type) && <FreeInputHint type={option.type} />}
      </CardContent>
    </Card>
  );
};

const PricingControls = ({ option, onUpdate }) => (
  <>
    <div className="flex items-center gap-2">
      <Label className="text-sm whitespace-nowrap">Pricing:</Label>
      <Select value={option.mechanism} onValueChange={(value) => onUpdate(option.id, 'mechanism', value)}>
        <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="variant"><div className="flex items-center gap-1"><Tag className="h-3 w-3" /> Variant</div></SelectItem>
          <SelectItem value="addon"><div className="flex items-center gap-1"><Plus className="h-3 w-3" /> Add-on</div></SelectItem>
        </SelectContent>
      </Select>
    </div>
    {option.mechanism === 'addon' && (
      <div className="flex items-center gap-2">
        <Label className="text-sm whitespace-nowrap">Duration:</Label>
        <Select value={option.multiplierType} onValueChange={(value) => onUpdate(option.id, 'multiplierType', value)}>
          <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="fixed">Fixed</SelectItem>
            <SelectItem value="temporal">Per Period</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )}
  </>
);

const PricingExplanation = ({ option }) => {
  if (option.priceImpact) {
    const text =
      option.mechanism === 'variant'
        ? 'Variant: Price replaces base price when selected'
        : option.multiplierType === 'temporal'
        ? 'Add-on (Per Period): Price multiplied by duration'
        : 'Add-on (Fixed): One-time charge added to total';
    return <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded">{text}</p>;
  }
  return (
    <p className="text-xs text-gray-500 bg-blue-50 p-2 rounded border border-blue-100">
      Info Only — This option collects customer input without affecting the price.
      The response will be included in the order/booking details for the vendor.
    </p>
  );
};

const ChoicesEditor = ({ option, onUpdateChoice, onAddChoice, onRemoveChoice }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium">{option.priceImpact ? 'Choices & Pricing' : 'Choices'}</Label>
    {option.choices.map((choice, choiceIndex) => (
      <div key={choiceIndex} className="flex items-center gap-2">
        <Input
          placeholder="Choice label"
          value={choice.label}
          onChange={(e) => {
            onUpdateChoice(option.id, choiceIndex, 'label', e.target.value);
            onUpdateChoice(option.id, choiceIndex, 'value', e.target.value.toLowerCase().replace(/\s+/g, '_'));
          }}
          className="flex-1"
        />
        {option.priceImpact && (
          <div className="relative w-28">
            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="number" min="0" step="1" placeholder="0"
              value={choice.price || ''}
              onChange={(e) => onUpdateChoice(option.id, choiceIndex, 'price', parseFloat(e.target.value) || 0)}
              className="pl-7"
            />
          </div>
        )}
        {option.choices.length > 1 && (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-500" onClick={() => onRemoveChoice(option.id, choiceIndex)}>
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    ))}
    <Button variant="outline" size="sm" onClick={() => onAddChoice(option.id)} className="mt-2">
      <Plus className="h-3 w-3 mr-1" /> Add Choice
    </Button>
  </div>
);

const FreeInputHint = ({ type }) => {
  const hints = {
    text: 'Customer will see a single-line text input field',
    textarea: 'Customer will see a multi-line text area for detailed requirements',
    number: 'Customer will see a numeric input field',
    date: 'Customer will see a date picker',
  };
  return (
    <div className="bg-gray-50 border rounded-md p-3">
      <Label className="text-sm text-gray-600">{hints[type]}</Label>
      <p className="text-xs text-gray-400 mt-1">The customer's response will be included in the order details.</p>
    </div>
  );
};

export default OptionEditorCard;
